import { useRef, useCallback, useState, useEffect } from 'react';
import { useSearch, useLocation } from 'wouter';
import { useCanvasStore } from './canvas-store';
import { useActiveProjectId, useActiveProjectActiveView, useActiveProjectIsCanvasOpen } from './store-selectors';
import { CanvasSidebar } from './canvas-sidebar';
import { CanvasChat } from './canvas-chat';
import { CanvasFlowWorkspace } from './canvas-flow-workspace';
import { CanvasToolbar } from './canvas-toolbar';
import { CanvasProjectView } from './canvas-project-view';
import { CanvasAnalyticsView } from './canvas-analytics-view';
import { CanvasAdsManagerView } from './canvas-ads-manager-view';
import { HeartbeatContent } from '@/pages/heartbeat';
import { CanvasBrandsView } from './canvas-brands-view';
import { CanvasAdTemplatesView } from './canvas-ad-templates-view';
import { CanvasAdLibraryView } from './canvas-ad-library-view';
import { CanvasAssetsView } from './canvas-assets-view';
import { DocumentOverlay } from './DocumentOverlay';
import { SidebarPanel } from './SidebarPanel';
import { SettingsModal } from '../../components/settings-modal';
import { ChevronRight, ImageIcon, Minimize2 } from 'lucide-react';
import { useAuthUser } from '@/App';

export function CanvasPage() {
  const {
    isFullscreen, isSidebarOpen, isSidebarCollapsed, chatWidth, setChatWidth, toggleSidebar,
    folderProjectId,
    setCanvasOpen, toggleFullscreen,
    showDocumentOverlay,
    showSidebarPanel,
    sidebarPanelContent,
    closeSidebarPanel,
    openSidebarPanel,
    lastBriefContent,
  } = useCanvasStore();
  const persistenceProjectId = useActiveProjectId();
  const activeView = useActiveProjectActiveView();
  const isCanvasOpen = useActiveProjectIsCanvasOpen();

  const search = useSearch();
  const [, navigate] = useLocation();
  const [settingsModal, setSettingsModal] = useState<{ open: boolean; initialTab?: 'connections' }>({ open: false });

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get('settings') === 'connections') {
      setSettingsModal({ open: true, initialTab: 'connections' });
      params.delete('settings');
      const newSearch = params.toString();
      navigate('/canvas' + (newSearch ? '?' + newSearch : ''), { replace: true });
    }
  }, [search]);

  // Listen for "open campaign brief" events dispatched from AgentStepCard brief_link buttons
  useEffect(() => {
    const handler = () => {
      const brief = useCanvasStore.getState().lastBriefContent;
      if (brief) {
        openSidebarPanel({ type: 'report', title: brief.title, markdown: brief.content });
      }
    };
    window.addEventListener('admoji:open-brief', handler);
    return () => window.removeEventListener('admoji:open-brief', handler);
  }, [openSidebarPanel]);

  // Delete selected canvas images when the user presses Delete or Backspace
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const tag = (e.target as HTMLElement)?.tagName ?? '';
      const editable = (e.target as HTMLElement)?.isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || editable) return;
      const { selectedImageIds: ids, deleteImages: del } = useCanvasStore.getState();
      if (ids.length > 0) {
        e.preventDefault();
        del(ids);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isDraggingHandle = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(chatWidth);

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      isDraggingHandle.current = true;
      dragStartX.current = e.clientX;
      dragStartWidth.current = chatWidth;
      e.preventDefault();

      const onMove = (ev: MouseEvent) => {
        if (!isDraggingHandle.current) return;
        const delta = ev.clientX - dragStartX.current;
        const newWidth = Math.max(300, Math.min(600, dragStartWidth.current + delta));
        setChatWidth(newWidth);
      };

      const onUp = () => {
        isDraggingHandle.current = false;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [chatWidth, setChatWidth]
  );

  const sidebarWidth = isSidebarOpen ? (isSidebarCollapsed ? 60 : 270) : 0;

  const authUser = useAuthUser();

  const isHeartbeatMode = activeView === 'heartbeat';
  // Ads Manager is admin-only until user-ready. The sidebar no longer offers
  // it to non-admins; this guard is defense-in-depth so a stray
  // activeView='ads-manager' (stale state, future links) can't render it —
  // non-admins fall through to the normal project/chat modes instead.
  const isAdsManagerMode = activeView === 'ads-manager' && !!authUser?.isAdmin;
  const isBrandsMode = activeView === 'brands';
  const isAnalyticsMode = activeView === 'analytics';
  const isAdTemplatesMode = activeView === 'ad-templates';
  const isAdLibraryMode = activeView === 'ad-library';
  const isAssetsMode = activeView === 'assets';
  const isProjectMode = activeView === 'project' || (!isHeartbeatMode && !isAdsManagerMode && !isAnalyticsMode && !isBrandsMode && !isAdTemplatesMode && !isAdLibraryMode && !isAssetsMode && !!folderProjectId && !persistenceProjectId && activeView !== 'chat-only' && activeView !== 'chat-canvas' && activeView !== 'new-task');
  const isChatOnlyMode = activeView === 'chat-only';
  const isChatCanvasMode = activeView === 'chat-canvas';

  return (
    <>
    <div
      className="flex h-screen overflow-hidden bg-[#EBEBEB]"
      style={{ letterSpacing: '-0.01em', WebkitFontSmoothing: 'antialiased' } as React.CSSProperties}
    >
      {/* Sidebar with animated fullscreen hide */}
      <div
        className="relative flex-shrink-0 h-full overflow-hidden transition-all duration-[250ms] ease-in-out"
        style={{
          width: isFullscreen ? 0 : sidebarWidth,
          opacity: isFullscreen ? 0 : 1,
        }}
      >
        <CanvasSidebar />
      </div>

      {/* Expand button shown when sidebar is collapsed */}
      {!isFullscreen && !isSidebarOpen && (
        <button
          data-testid="canvas-sidebar-expand"
          onClick={toggleSidebar}
          className="absolute top-4 left-2 z-20 w-8 h-8 bg-white border border-[#E8E5E0] rounded-lg flex items-center justify-center text-[#8C8C8C] hover:text-[#1A1A1A] shadow-sm transition-colors"
          style={{ zIndex: 20 }}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* Exit fullscreen button — floats in top-right of canvas in fullscreen mode */}
      {isFullscreen && (
        <button
          data-testid="canvas-exit-fullscreen"
          onClick={toggleFullscreen}
          title="Exit fullscreen (Cmd+\\)"
          className="absolute top-4 right-4 z-50 flex items-center gap-1.5 rounded-lg bg-white border border-[#E8E5E0] shadow-sm transition-all"
          style={{ padding: '5px 10px', fontSize: 12, color: '#6B675F', fontWeight: 500 }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
        >
          <Minimize2 className="w-3.5 h-3.5" />
          Exit fullscreen
        </button>
      )}

      {isHeartbeatMode ? (
        /* Mode 1: Heartbeat AI embedded view */
        <div className="flex-1 overflow-auto" style={{ backgroundColor: '#F8F8F7' }}>
          <div className="p-6 max-w-full">
            <HeartbeatContent />
          </div>
        </div>
      ) : isAdsManagerMode ? (
        /* Mode 1b: Ads Manager embedded view */
        <div className="flex-1 overflow-auto" style={{ background: '#F0F0EF' }}>
          <CanvasAdsManagerView />
        </div>
      ) : isAdTemplatesMode ? (
        /* Mode 1c: Ad Templates embedded view */
        <div className="flex-1 overflow-auto" style={{ background: '#F0F0EF' }}>
          <CanvasAdTemplatesView />
        </div>
      ) : isAdLibraryMode ? (
        /* Mode 1d: Ad Library unified embedded view */
        <div className="flex-1 overflow-hidden" style={{ background: '#F0F0EF' }}>
          <CanvasAdLibraryView />
        </div>
      ) : isAssetsMode ? (
        /* Mode 1e: Assets (brand folders) embedded view */
        <div className="flex-1 overflow-hidden" style={{ background: '#F0F0EF' }}>
          <CanvasAssetsView />
        </div>
      ) : isBrandsMode ? (
        /* Mode 1c: Brands embedded view */
        <div className="flex-1 overflow-auto" style={{ backgroundColor: '#F8F8F7' }}>
          <CanvasBrandsView />
        </div>
      ) : isAnalyticsMode ? (
        /* Mode 1b: Analytics embedded view */
        <div className="flex-1 overflow-auto" style={{ backgroundColor: '#F0F0EF' }}>
          <CanvasAnalyticsView />
        </div>
      ) : isProjectMode ? (
        /* Mode 3: Full-width project overview */
        <div className="flex-1 overflow-hidden">
          <CanvasProjectView />
        </div>
      ) : isChatOnlyMode ? (
        /* Mode 3: Chat-only, full-width centered — with optional report/sidebar panel */
        <div className="flex flex-1 overflow-hidden relative">
          <div
            className="h-full overflow-hidden transition-all duration-300 ease-in-out"
            style={{ flex: (showDocumentOverlay || showSidebarPanel) ? '0 0 50%' : '1 1 0%', minWidth: 0 }}
          >
            <CanvasChat fullWidth={!showDocumentOverlay && !showSidebarPanel} />
          </div>
          {showDocumentOverlay && (
            <>
              <div className="flex-shrink-0 h-full" style={{ width: 1, background: '#DCDCDC' }} />
              <div
                className="h-full overflow-hidden transition-all duration-300 ease-in-out"
                style={{ flex: 1, minWidth: 360, background: '#fff' }}
                data-testid="report-panel"
              >
                <DocumentOverlay key={persistenceProjectId ?? "new"} />
              </div>
            </>
          )}
          {showSidebarPanel && !showDocumentOverlay && (
            <>
              <div className="flex-shrink-0 h-full" style={{ width: 1, background: '#DCDCDC' }} />
              <div
                className="h-full overflow-hidden transition-all duration-300 ease-in-out"
                style={{ flex: 1, minWidth: 360, background: '#fff' }}
                data-testid="sidebar-split-panel"
              >
                <SidebarPanel key={persistenceProjectId ?? "new"} open={true} content={sidebarPanelContent} onClose={closeSidebarPanel} inline />
              </div>
            </>
          )}
        </div>
      ) : isChatCanvasMode ? (
        /* Mode 4: Chat + Canvas side by side (canvas slides in/out; chat expands to fill when canvas hidden) */
        <div className="flex flex-1 overflow-hidden relative">
          {/* Chat panel — expands to full width when canvas is hidden */}
          <div
            data-testid="canvas-chat-panel"
            className="h-full overflow-hidden transition-all duration-300 ease-in-out relative"
            style={{
              flex: isCanvasOpen && !isFullscreen ? '0 0 ' + chatWidth + 'px' : '1 1 0%',
              minWidth: 0,
              opacity: isFullscreen ? 0 : 1,
              pointerEvents: isFullscreen ? 'none' : 'auto',
            }}
          >
            <CanvasChat fullWidth={!isCanvasOpen} />

            {/* Show canvas pill — appears when canvas is hidden */}
            {!isCanvasOpen && (
              <button
                data-testid="canvas-show-pill"
                onClick={() => setCanvasOpen(true)}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium shadow-md transition-all"
                style={{
                  background: '#1A1A1A',
                  color: '#FFFFFF',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <ImageIcon className="w-3 h-3" />
                Show canvas
              </button>
            )}
          </div>

          {/* Drag handle — only when canvas is open */}
          {isCanvasOpen && (
            <div
              data-testid="canvas-chat-resize-handle"
              className="flex-shrink-0 h-full relative cursor-col-resize group overflow-hidden transition-all duration-[250ms] ease-in-out"
              style={{
                width: isFullscreen ? 0 : 1,
                opacity: isFullscreen ? 0 : 1,
                pointerEvents: isFullscreen ? 'none' : 'auto',
              }}
              onMouseDown={handleDragStart}
            >
              <div className="absolute inset-0 bg-[#DCDCDC] group-hover:bg-[#C0C0C0] group-active:bg-[#A8A49E] transition-colors" style={{ opacity: 0.5 }} />
            </div>
          )}

          {/* Canvas workspace or report panel — slides in/out */}
          <div
            className="h-full relative overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              flex: (isCanvasOpen || showDocumentOverlay || showSidebarPanel) ? 1 : 0,
              width: (isCanvasOpen || showDocumentOverlay || showSidebarPanel) ? undefined : 0,
              opacity: (isCanvasOpen || showDocumentOverlay || showSidebarPanel) ? 1 : 0,
              pointerEvents: (isCanvasOpen || showDocumentOverlay || showSidebarPanel) ? 'auto' : 'none',
              background: (showDocumentOverlay || showSidebarPanel) ? '#fff' : '#EBEBEB',
            }}
          >
            {showDocumentOverlay ? (
              <DocumentOverlay key={persistenceProjectId ?? "new"} />
            ) : showSidebarPanel && sidebarPanelContent?.type !== 'creative_edit' && sidebarPanelContent?.type !== 'creative_layers' ? (
              <SidebarPanel key={persistenceProjectId ?? "new"} open={true} content={sidebarPanelContent} onClose={closeSidebarPanel} inline />
            ) : (
              <>
                {/* v5.77 — creative_edit keeps the CANVAS visible (the edit
                    happens in place on the tile) and docks the controls as a
                    right-edge overlay instead of replacing the workspace. */}
                {showSidebarPanel && (sidebarPanelContent?.type === 'creative_edit' || sidebarPanelContent?.type === 'creative_layers') && (
                  <div
                    data-testid="creative-edit-dock"
                    style={{
                      position: 'absolute', top: 0, right: 0, bottom: 0, width: 320, zIndex: 30,
                      background: '#fff', borderLeft: '1px solid #E5E2DC',
                      boxShadow: '-8px 0 24px rgba(0,0,0,0.08)',
                    }}
                  >
                    <SidebarPanel key={persistenceProjectId ?? "new"} open={true} content={sidebarPanelContent} onClose={closeSidebarPanel} inline />
                  </div>
                )}
                <button
                  data-testid="canvas-close-btn"
                  onClick={() => setCanvasOpen(false)}
                  className="absolute top-3 right-3 z-20 w-7 h-7 flex items-center justify-center rounded-lg text-[#8C8578] hover:text-[#1A1A1A] hover:bg-white transition-all"
                  style={{ border: '1px solid #E5E2DC', background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(4px)' }}
                  title="Hide canvas"
                >
                  <span style={{ fontSize: 14, lineHeight: 1 }}>✕</span>
                </button>
                <CanvasFlowWorkspace />
                <CanvasToolbar />
              </>
            )}
          </div>
        </div>
      ) : (
        /* Mode 5: new-task / fallback — chat-first, full-width centered */
        <div className="flex flex-1 overflow-hidden relative">
          <div
            className="h-full overflow-hidden transition-all duration-300 ease-in-out"
            style={{ flex: (showDocumentOverlay || showSidebarPanel) ? '0 0 50%' : '1 1 0%', minWidth: 0 }}
          >
            <CanvasChat fullWidth={!showDocumentOverlay && !showSidebarPanel} />
          </div>
          {showDocumentOverlay && (
            <>
              <div className="flex-shrink-0 h-full" style={{ width: 1, background: '#DCDCDC' }} />
              <div
                className="h-full overflow-hidden transition-all duration-300 ease-in-out"
                style={{ flex: 1, minWidth: 360, background: '#fff' }}
                data-testid="report-panel"
              >
                <DocumentOverlay key={persistenceProjectId ?? "new"} />
              </div>
            </>
          )}
          {showSidebarPanel && !showDocumentOverlay && (
            <>
              <div className="flex-shrink-0 h-full" style={{ width: 1, background: '#DCDCDC' }} />
              <div
                className="h-full overflow-hidden transition-all duration-300 ease-in-out"
                style={{ flex: 1, minWidth: 360, background: '#fff' }}
                data-testid="sidebar-split-panel"
              >
                <SidebarPanel key={persistenceProjectId ?? "new"} open={true} content={sidebarPanelContent} onClose={closeSidebarPanel} inline />
              </div>
            </>
          )}
        </div>
      )}

    </div>

    {settingsModal.open && (
      <SettingsModal
        initialTab={settingsModal.initialTab}
        onClose={() => setSettingsModal({ open: false })}
      />
    )}

    </>
  );
}

export default CanvasPage;
